import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Progress } from "../../components/ui/progress";
import { 
  Users, UserPlus, Clock, DollarSign, FileText, CheckCircle, XCircle,
  Calendar, Briefcase, Building2, ClipboardList, Calculator, Search,
  Upload, Wallet, Plus, ArrowUpCircle, ArrowDownCircle, Eye, Download, Trash2,
  CreditCard, Edit, FileSpreadsheet, Laptop, Monitor, Package, UserCheck
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentHR() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [onboardingTasks, setOnboardingTasks] = useState([]);
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [payrollFunding, setPayrollFunding] = useState({ balance: 0, transactions: [] });
  const [equipment, setEquipment] = useState([]);
  const [equipmentStats, setEquipmentStats] = useState(null);
  
  const [activeTab, setActiveTab] = useState("employees");
  const [search, setSearch] = useState("");
  
  // Dialog states
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [payrollDialogOpen, setPayrollDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [employeeDetailOpen, setEmployeeDetailOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [docReqDialogOpen, setDocReqDialogOpen] = useState(false);
  const [fundingDialogOpen, setFundingDialogOpen] = useState(false);
  const [payCommissionDialogOpen, setPayCommissionDialogOpen] = useState(false);
  const [payEmployeeDialogOpen, setPayEmployeeDialogOpen] = useState(false);
  const [viewDocsDialogOpen, setViewDocsDialogOpen] = useState(false);
  const [selectedEmployeeDocs, setSelectedEmployeeDocs] = useState(null);
  const [editEmployeeDialogOpen, setEditEmployeeDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [stripeFundingDialogOpen, setStripeFundingDialogOpen] = useState(false);
  const [stripeFundingAmount, setStripeFundingAmount] = useState(1000);
  const [processingStripe, setProcessingStripe] = useState(false);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [assignEquipmentOpen, setAssignEquipmentOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  
  // Forms - Updated for direct employee creation
  const [employeeForm, setEmployeeForm] = useState({
    full_name: "", email: "", phone: "", employment_type: "full_time", 
    department_id: "", job_title: "", manager_id: "", hire_date: "", 
    salary_type: "hourly", salary_amount: 0, pay_frequency: "biweekly",
    create_system_account: true
  });
  
  const [editEmployeeForm, setEditEmployeeForm] = useState({
    full_name: "", email: "", phone: "", employment_type: "full_time", 
    department_id: "", job_title: "", manager_id: "",
    salary_type: "hourly", salary_amount: 0, pay_frequency: "biweekly",
    pto_balance: 0, sick_balance: 0, status: "active"
  });
  
  const [departmentForm, setDepartmentForm] = useState({
    name: "", description: "", manager_id: ""
  });
  
  const [payrollForm, setPayrollForm] = useState({
    employee_id: "", pay_period_start: "", pay_period_end: "",
    regular_hours: 0, overtime_hours: 0, gross_pay: 0, deductions: 0, net_pay: 0, notes: ""
  });
  
  const [taskForm, setTaskForm] = useState({
    title: "", description: "", category: "general", required: true, order: 0
  });

  const [docReqForm, setDocReqForm] = useState({
    name: "", description: "", document_type: "other", required: true, order: 0
  });

  const [fundingForm, setFundingForm] = useState({ amount: 0, description: "" });
  const [commissionForm, setCommissionForm] = useState({ agent_id: "", amount: 0, description: "", reference: "" });
  const [payEmployeeForm, setPayEmployeeForm] = useState({ employee_id: "", amount: 0, payment_type: "regular", description: "", reference: "" });
  const [equipmentForm, setEquipmentForm] = useState({
    name: "", model: "", serial_number: "", category: "laptop", condition: "new",
    purchase_date: "", purchase_price: 0, notes: ""
  });
  const [assignForm, setAssignForm] = useState({ employee_id: "", notes: "" });
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Stripe callback
  useEffect(() => {
    const funding = searchParams.get("funding");
    const sessionId = searchParams.get("session_id");
    
    if (funding === "success" && sessionId) {
      confirmStripeFunding(sessionId);
      // Clear URL params
      window.history.replaceState({}, '', '/agent/hr');
    } else if (funding === "cancelled") {
      toast.error("Payment was cancelled");
      window.history.replaceState({}, '', '/agent/hr');
    }
  }, [searchParams]);

  const confirmStripeFunding = async (sessionId) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/hr/payroll/funding/confirm/${sessionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.already_processed) {
        toast.info("Payment was already processed");
      } else {
        toast.success(`$${response.data.amount.toFixed(2)} added to payroll via Stripe!`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm payment");
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const [statsRes, employeesRes, deptsRes, managersRes, timeOffRes, payrollRes, tasksRes, docReqRes, fundingRes, agentsRes, equipRes, equipStatsRes] = await Promise.all([
        axios.get(`${API}/hr/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/departments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/employees/managers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/time-off`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/payroll`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/onboarding/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/document-requirements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/hr/payroll/funding`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { balance: 0, transactions: [] } })),
        axios.get(`${API}/agent/team`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/hr/equipment`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/hr/equipment/stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { total: 0, available: 0, assigned: 0 } }))
      ]);
      
      setStats(statsRes.data);
      setEmployees(employeesRes.data);
      setDepartments(deptsRes.data);
      setManagers(managersRes.data);
      setTimeOffRequests(timeOffRes.data);
      setPayrollRecords(payrollRes.data);
      setOnboardingTasks(tasksRes.data);
      setDocumentRequirements(docReqRes.data);
      setPayrollFunding(fundingRes.data);
      setAgents(agentsRes.data);
      setEquipment(equipRes.data);
      setEquipmentStats(equipStatsRes.data);
    } catch (error) {
      console.error("Error fetching HR data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmployee = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/employees`, employeeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Employee created successfully");
      setEmployeeDialogOpen(false);
      setEmployeeForm({ 
        full_name: "", email: "", phone: "", employment_type: "full_time", 
        department_id: "", job_title: "", manager_id: "", hire_date: "", 
        salary_type: "hourly", salary_amount: 0, pay_frequency: "biweekly",
        create_system_account: true
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create employee");
    }
  };

  const handleCreateDepartment = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      if (editingDepartment) {
        await axios.put(`${API}/hr/departments/${editingDepartment.department_id}`, departmentForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Department updated");
      } else {
        await axios.post(`${API}/hr/departments`, departmentForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Department created");
      }
      setDepartmentDialogOpen(false);
      setEditingDepartment(null);
      setDepartmentForm({ name: "", description: "", manager_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save department");
    }
  };

  const handleDeleteDepartment = async (deptId) => {
    if (!confirm("Are you sure you want to delete this department?")) return;
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/hr/departments/${deptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Department deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete department");
    }
  };

  const openEditDepartment = (dept) => {
    setEditingDepartment(dept);
    setDepartmentForm({ name: dept.name, description: dept.description || "", manager_id: dept.manager_id || "" });
    setDepartmentDialogOpen(true);
  };

  const handleCreatePayroll = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/payroll`, payrollForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payroll entry created");
      setPayrollDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payroll");
    }
  };

  const handleProcessPayroll = async (payrollId) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/hr/payroll/${payrollId}/process`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payroll processed! $${response.data.amount_disbursed?.toFixed(2)} disbursed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process payroll");
    }
  };

  const handleApprovePayroll = async (payrollId) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/payroll/${payrollId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payroll approved - Ready for processing");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve payroll");
    }
  };

  const handleRejectPayroll = async (payrollId, reason) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/payroll/${payrollId}/reject`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payroll rejected");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject payroll");
    }
  };

  const handleEditPayroll = async (payrollId, updates) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/hr/payroll/${payrollId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payroll updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update payroll");
    }
  };

  const handleCalculateFromClock = async () => {
    if (!payrollForm.employee_id || !payrollForm.pay_period_start || !payrollForm.pay_period_end) {
      toast.error("Select employee and date range first");
      return;
    }
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/hr/payroll/calculate-from-clock?employee_id=${payrollForm.employee_id}&start_date=${payrollForm.pay_period_start}&end_date=${payrollForm.pay_period_end}`,
        {}, { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayrollForm(prev => ({ ...prev, ...response.data }));
      toast.success("Calculated from clock records");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate");
    }
  };

  const handleApproveTimeOff = async (requestId) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/time-off/${requestId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Time off approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve");
    }
  };

  const handleRejectTimeOff = async (requestId) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/time-off/${requestId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Time off rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject");
    }
  };

  const handleCreateTask = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/onboarding/tasks`, taskForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Onboarding task created");
      setTaskDialogOpen(false);
      setTaskForm({ title: "", description: "", category: "general", required: true, order: 0 });
      fetchData();
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const handleSeedTasks = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/hr/seed-onboarding-tasks`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to seed tasks");
    }
  };

  const handleCompleteTask = async (employeeId, taskId) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/employees/${employeeId}/onboarding/${taskId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Task marked complete");
      // Refresh employee detail
      const response = await axios.get(`${API}/hr/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmployee(response.data);
      fetchData();
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };

  const openEmployeeDetail = async (employee) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/hr/employees/${employee.hr_employee_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmployee(response.data);
      setEmployeeDetailOpen(true);
    } catch (error) {
      toast.error("Failed to load employee details");
    }
  };

  // Edit Employee handlers
  const openEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEditEmployeeForm({
      full_name: employee.full_name || "",
      email: employee.email || "",
      phone: employee.phone || "",
      employment_type: employee.employment_type || "full_time",
      department_id: employee.department_id || "",
      job_title: employee.job_title || "",
      manager_id: employee.manager_id || "",
      salary_type: employee.salary_type || "hourly",
      salary_amount: employee.salary_amount || 0,
      pay_frequency: employee.pay_frequency || "biweekly",
      pto_balance: employee.pto_balance || 0,
      sick_balance: employee.sick_balance || 0,
      status: employee.status || "active"
    });
    setEditEmployeeDialogOpen(true);
  };

  const handleUpdateEmployee = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/hr/employees/${editingEmployee.hr_employee_id}`, editEmployeeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Employee updated");
      setEditEmployeeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update employee");
    }
  };

  // Stripe Funding handlers
  const handleStripeFunding = async () => {
    if (stripeFundingAmount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    
    setProcessingStripe(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/hr/payroll/funding/checkout?amount=${stripeFundingAmount}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checkout session");
      setProcessingStripe(false);
    }
  };

  // Export handlers
  const handleExport = async (type) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/hr/export/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} data exported`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    }
  };

  const filteredEmployees = employees.filter(e => 
    e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase()) ||
    e.department?.toLowerCase().includes(search.toLowerCase()) ||
    e.job_title?.toLowerCase().includes(search.toLowerCase())
  );

  // Document Requirements handlers
  const handleCreateDocReq = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/document-requirements`, docReqForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document requirement created");
      setDocReqDialogOpen(false);
      setDocReqForm({ name: "", description: "", document_type: "other", required: true, order: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create requirement");
    }
  };

  const handleDeleteDocReq = async (reqId) => {
    if (!confirm("Delete this document requirement?")) return;
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/hr/document-requirements/${reqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Requirement deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete requirement");
    }
  };

  const handleSeedDocReqs = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/hr/seed-document-requirements`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to seed requirements");
    }
  };

  // Payroll Funding handlers
  const handleAddFunding = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/payroll/funding`, { ...fundingForm, funding_type: "deposit" }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Funding added successfully");
      setFundingDialogOpen(false);
      setFundingForm({ amount: 0, description: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add funding");
    }
  };

  const handlePayCommission = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/payroll/pay-commission`, commissionForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Commission paid successfully");
      setPayCommissionDialogOpen(false);
      setCommissionForm({ agent_id: "", amount: 0, description: "", reference: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pay commission");
    }
  };

  const handlePayEmployee = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/payroll/pay-employee`, payEmployeeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment processed successfully");
      setPayEmployeeDialogOpen(false);
      setPayEmployeeForm({ employee_id: "", amount: 0, payment_type: "regular", description: "", reference: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process payment");
    }
  };

  // View Employee Documents
  const openViewDocs = async (employee) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/hr/employees/${employee.hr_employee_id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmployeeDocs({ ...employee, ...response.data });
      setViewDocsDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load documents");
    }
  };

  const handleUpdateDocStatus = async (employeeId, docId, status) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/hr/employees/${employeeId}/documents/${docId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Document ${status}`);
      // Refresh
      const response = await axios.get(`${API}/hr/employees/${employeeId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEmployeeDocs(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const downloadDocument = async (employeeId, docId, filename) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/hr/employees/${employeeId}/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  // Equipment Handlers
  const handleAddEquipment = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/equipment`, equipmentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Equipment added");
      setEquipmentDialogOpen(false);
      setEquipmentForm({ name: "", model: "", serial_number: "", category: "laptop", condition: "new", purchase_date: "", purchase_price: 0, notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add equipment");
    }
  };

  const handleAssignEquipment = async () => {
    if (!selectedEquipment || !assignForm.employee_id) return;
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/equipment/${selectedEquipment.equipment_id}/assign`, assignForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Equipment assigned");
      setAssignEquipmentOpen(false);
      setSelectedEquipment(null);
      setAssignForm({ employee_id: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign equipment");
    }
  };

  const handleUnassignEquipment = async (equipmentId) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/hr/equipment/${equipmentId}/unassign`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Equipment unassigned");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unassign equipment");
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm("Are you sure you want to delete this equipment?")) return;
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/hr/equipment/${equipmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Equipment deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete equipment");
    }
  };

  const handleSeedEquipment = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const res = await axios.post(`${API}/hr/equipment/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to seed equipment");
    }
  };

  const openAssignDialog = (equip) => {
    setSelectedEquipment(equip);
    setAssignForm({ employee_id: "", notes: "" });
    setAssignEquipmentOpen(true);
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>HR Management</h1>
            <p className="text-muted-foreground">Employee onboarding, time off, and payroll</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="card-organic">
              <CardContent className="p-4">
                <Users className="h-6 w-6 text-primary mb-2" />
                <p className="text-2xl font-bold">{stats.total_employees}</p>
                <p className="text-xs text-muted-foreground">Total Employees</p>
              </CardContent>
            </Card>
            <Card className="card-organic bg-green-50">
              <CardContent className="p-4">
                <CheckCircle className="h-6 w-6 text-green-600 mb-2" />
                <p className="text-2xl font-bold text-green-800">{stats.active_employees}</p>
                <p className="text-xs text-green-600">Active</p>
              </CardContent>
            </Card>
            <Card className="card-organic bg-blue-50">
              <CardContent className="p-4">
                <ClipboardList className="h-6 w-6 text-blue-600 mb-2" />
                <p className="text-2xl font-bold text-blue-800">{stats.onboarding}</p>
                <p className="text-xs text-blue-600">Onboarding</p>
              </CardContent>
            </Card>
            <Card className="card-organic bg-orange-50">
              <CardContent className="p-4">
                <Calendar className="h-6 w-6 text-orange-600 mb-2" />
                <p className="text-2xl font-bold text-orange-800">{stats.pending_time_off}</p>
                <p className="text-xs text-orange-600">Pending Time Off</p>
              </CardContent>
            </Card>
            <Card className="card-organic bg-purple-50">
              <CardContent className="p-4">
                <DollarSign className="h-6 w-6 text-purple-600 mb-2" />
                <p className="text-2xl font-bold text-purple-800">{stats.pending_payroll}</p>
                <p className="text-xs text-purple-600">Pending Payroll</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap">
            <TabsTrigger value="employees">Employees</TabsTrigger>
            <TabsTrigger value="departments">Departments</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="time-off">Time Off</TabsTrigger>
            <TabsTrigger value="payroll">Payroll</TabsTrigger>
            <TabsTrigger value="funding">Funding</TabsTrigger>
            <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
            <TabsTrigger value="equipment">Equipment</TabsTrigger>
          </TabsList>

          {/* Employees Tab */}
          <TabsContent value="employees" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Employees</CardTitle>
                  <CardDescription>Manage employee HR records</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleExport('employees')} data-testid="export-employees-btn">
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                  <Button onClick={() => setEmployeeDialogOpen(true)} data-testid="add-employee-btn">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Employee
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search employees..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
                </div>
                
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Department</th>
                        <th>Job Title</th>
                        <th>Manager</th>
                        <th>Type</th>
                        <th>Onboarding</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployees.map((emp) => (
                        <tr key={emp.hr_employee_id}>
                          <td>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                {emp.full_name?.[0] || "?"}
                              </div>
                              <div>
                                <p className="font-medium">{emp.full_name || "Unknown"}</p>
                                <p className="text-xs text-muted-foreground">{emp.email}</p>
                              </div>
                            </div>
                          </td>
                          <td>{emp.department || "-"}</td>
                          <td>{emp.job_title || "-"}</td>
                          <td>{emp.manager?.full_name || "-"}</td>
                          <td>
                            <Badge variant="outline" className="capitalize">{emp.employment_type?.replace("_", " ")}</Badge>
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Progress value={emp.onboarding_progress?.percentage || 0} className="w-20 h-2" />
                              <span className="text-xs">{emp.onboarding_progress?.percentage || 0}%</span>
                            </div>
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" onClick={() => openEmployeeDetail(emp)} data-testid={`view-employee-${emp.hr_employee_id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openEditEmployee(emp)} data-testid={`edit-employee-${emp.hr_employee_id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openViewDocs(emp)} data-testid={`view-docs-${emp.hr_employee_id}`}>
                                <FileText className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Departments</CardTitle>
                  <CardDescription>Manage company departments and their managers</CardDescription>
                </div>
                <Button onClick={() => { setEditingDepartment(null); setDepartmentForm({ name: "", description: "", manager_id: "" }); setDepartmentDialogOpen(true); }} data-testid="add-department-btn">
                  <Building2 className="h-4 w-4 mr-2" />
                  Add Department
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {departments.map((dept) => (
                    <Card key={dept.department_id} className="card-organic">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{dept.name}</h3>
                            {dept.description && <p className="text-sm text-muted-foreground mt-1">{dept.description}</p>}
                          </div>
                          <Badge variant="outline">{dept.employee_count || 0} employees</Badge>
                        </div>
                        {dept.manager && (
                          <div className="mt-3 flex items-center gap-2 text-sm">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>Manager: {dept.manager.full_name}</span>
                          </div>
                        )}
                        <div className="mt-4 flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openEditDepartment(dept)}>Edit</Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteDepartment(dept.department_id)}>Delete</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {departments.length === 0 && (
                    <div className="col-span-full text-center text-muted-foreground py-12">
                      <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No departments created yet.</p>
                      <p className="text-sm">Create departments to organize your employees.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Document Requirements</CardTitle>
                  <CardDescription>Define what documents employees must provide during onboarding</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSeedDocReqs} data-testid="seed-doc-reqs-btn">Seed Default</Button>
                  <Button onClick={() => setDocReqDialogOpen(true)} data-testid="add-doc-req-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Requirement
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {documentRequirements.sort((a, b) => a.order - b.order).map((req) => (
                    <div key={req.requirement_id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <Badge variant="outline" className="capitalize">{req.document_type}</Badge>
                        <div>
                          <p className="font-medium">{req.name}</p>
                          <p className="text-sm text-muted-foreground">{req.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {req.required && <Badge variant="secondary">Required</Badge>}
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteDocReq(req.requirement_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {documentRequirements.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No document requirements defined.</p>
                      <p className="text-sm">Click "Seed Default" to create standard requirements.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time Off Tab */}
          <TabsContent value="time-off" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Time Off Requests</CardTitle>
                  <CardDescription>Approve or reject employee time off requests</CardDescription>
                </div>
                <Button variant="outline" onClick={() => handleExport('time-off')} data-testid="export-timeoff-btn">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Type</th>
                        <th>Dates</th>
                        <th>Hours</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeOffRequests.map((req) => (
                        <tr key={req.request_id}>
                          <td>{req.employee?.full_name || "Unknown"}</td>
                          <td><Badge variant={req.type === "pto" ? "default" : "secondary"}>{req.type.toUpperCase()}</Badge></td>
                          <td>{req.start_date} - {req.end_date}</td>
                          <td>{req.hours}h</td>
                          <td>
                            <Badge className={req.status === "approved" ? "bg-green-100 text-green-800" : req.status === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}>
                              {req.status}
                            </Badge>
                          </td>
                          <td>
                            {req.status === "pending" && (
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleApproveTimeOff(req.request_id)}>
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRejectTimeOff(req.request_id)}>
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {timeOffRequests.length === 0 && (
                        <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No time off requests</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payroll Tab */}
          <TabsContent value="payroll" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Payroll</CardTitle>
                  <CardDescription>Manage employee payroll and paystubs</CardDescription>
                </div>
                <Button onClick={() => setPayrollDialogOpen(true)} data-testid="add-payroll-btn">
                  <DollarSign className="h-4 w-4 mr-2" />
                  New Payroll Entry
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Pay Period</th>
                        <th>Hours</th>
                        <th>Gross</th>
                        <th>Net</th>
                        <th>Status</th>
                        <th>Approval</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollRecords.map((rec) => (
                        <tr key={rec.payroll_id}>
                          <td>{rec.employee_name}</td>
                          <td>{rec.pay_period_start} - {rec.pay_period_end}</td>
                          <td>{rec.regular_hours + rec.overtime_hours}h</td>
                          <td>${rec.gross_pay?.toFixed(2)}</td>
                          <td>${rec.net_pay?.toFixed(2)}</td>
                          <td>
                            <Badge className={
                              rec.status === "processed" ? "bg-green-100 text-green-800" : 
                              rec.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                              "bg-gray-100 text-gray-800"
                            }>
                              {rec.status}
                            </Badge>
                          </td>
                          <td>
                            <Badge className={
                              rec.approval_status === "approved" ? "bg-green-100 text-green-800" : 
                              rec.approval_status === "rejected" ? "bg-red-100 text-red-800" :
                              "bg-orange-100 text-orange-800"
                            }>
                              {rec.approval_status || "pending"}
                            </Badge>
                          </td>
                          <td className="space-x-1">
                            {rec.status === "pending" && rec.approval_status !== "approved" && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleApprovePayroll(rec.payroll_id)}
                                  className="text-green-600 hover:bg-green-50"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    const reason = window.prompt("Rejection reason:");
                                    if (reason !== null) handleRejectPayroll(rec.payroll_id, reason);
                                  }}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            {rec.status === "pending" && rec.approval_status === "approved" && (
                              <Button 
                                size="sm" 
                                onClick={() => handleProcessPayroll(rec.payroll_id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <DollarSign className="h-3 w-3 mr-1" />
                                Process
                              </Button>
                            )}
                            {rec.status !== "processed" && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                  const newNetPay = window.prompt("Edit Net Pay:", rec.net_pay);
                                  if (newNetPay !== null && !isNaN(parseFloat(newNetPay))) {
                                    handleEditPayroll(rec.payroll_id, { net_pay: parseFloat(newNetPay) });
                                  }
                                }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {payrollRecords.length === 0 && (
                        <tr><td colSpan={8} className="text-center text-muted-foreground py-8">No payroll records</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payroll Funding Tab */}
          <TabsContent value="funding" className="mt-4">
            <div className="space-y-6">
              {/* Balance Card */}
              <Card className="card-organic bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700 font-medium">Payroll Account Balance</p>
                      <p className="text-4xl font-bold text-green-800">${payrollFunding.balance?.toFixed(2) || '0.00'}</p>
                    </div>
                    <Wallet className="h-12 w-12 text-green-600 opacity-50" />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button onClick={() => setStripeFundingDialogOpen(true)} className="bg-green-600 hover:bg-green-700" data-testid="add-funds-stripe-btn">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Add Funds (Card)
                    </Button>
                    <Button variant="outline" onClick={() => setFundingDialogOpen(true)} data-testid="add-funding-btn">
                      <ArrowUpCircle className="h-4 w-4 mr-2" />
                      Manual Deposit
                    </Button>
                    <Button variant="outline" onClick={() => setPayCommissionDialogOpen(true)} data-testid="pay-commission-btn">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Pay Commission
                    </Button>
                    <Button variant="outline" onClick={() => setPayEmployeeDialogOpen(true)} data-testid="pay-employee-btn">
                      <Users className="h-4 w-4 mr-2" />
                      Pay Employee
                    </Button>
                  </div>
                  <div className="flex gap-2 mt-4 border-t pt-4">
                    <Button variant="ghost" size="sm" onClick={() => handleExport('payroll')} data-testid="export-payroll-btn">
                      <FileSpreadsheet className="h-4 w-4 mr-1" />
                      Export Transactions
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleExport('transfers')} data-testid="export-transfers-btn">
                      <FileSpreadsheet className="h-4 w-4 mr-1" />
                      Export ACH Transfers
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Transaction History */}
              <Card>
                <CardHeader>
                  <CardTitle>Transaction History</CardTitle>
                  <CardDescription>Recent payroll account activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Recipient</th>
                          <th>Amount</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payrollFunding.transactions?.map((tx) => (
                          <tr key={tx.transaction_id}>
                            <td>{new Date(tx.created_at).toLocaleDateString()}</td>
                            <td>
                              <Badge className={tx.type === "credit" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                {tx.type === "credit" ? <ArrowUpCircle className="h-3 w-3 inline mr-1" /> : <ArrowDownCircle className="h-3 w-3 inline mr-1" />}
                                {tx.payment_type || tx.funding_type || tx.type}
                              </Badge>
                            </td>
                            <td>{tx.description}</td>
                            <td>{tx.recipient_name || "-"}</td>
                            <td className={tx.type === "credit" ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                              {tx.type === "credit" ? "+" : "-"}${tx.amount?.toFixed(2)}
                            </td>
                            <td>${tx.balance_after?.toFixed(2)}</td>
                          </tr>
                        ))}
                        {(!payrollFunding.transactions || payrollFunding.transactions.length === 0) && (
                          <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No transactions yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Onboarding Tasks Tab */}
          <TabsContent value="onboarding" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Onboarding Task Templates</CardTitle>
                  <CardDescription>Define tasks for new employee onboarding</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSeedTasks}>Seed Default Tasks</Button>
                  <Button onClick={() => setTaskDialogOpen(true)}>Add Task</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {onboardingTasks.sort((a, b) => a.order - b.order).map((task) => (
                    <div key={task.task_id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{task.category}</Badge>
                        <div>
                          <p className="font-medium">{task.title}</p>
                          <p className="text-sm text-muted-foreground">{task.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {task.required && <Badge variant="secondary">Required</Badge>}
                      </div>
                    </div>
                  ))}
                  {onboardingTasks.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No onboarding tasks. Click "Seed Default Tasks" to get started.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Equipment Tab */}
          <TabsContent value="equipment" className="mt-4">
            <Card className="card-organic">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Laptop className="h-5 w-5" />
                      Equipment Inventory
                    </CardTitle>
                    <CardDescription>Track and manage company equipment assigned to employees</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleSeedEquipment} data-testid="seed-equipment-btn">
                      <Package className="h-4 w-4 mr-2" />
                      Seed Chromebooks
                    </Button>
                    <Button onClick={() => setEquipmentDialogOpen(true)} data-testid="add-equipment-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Equipment
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Stats */}
                {equipmentStats && (
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <Card className="bg-muted/50">
                      <CardContent className="p-4 text-center">
                        <Package className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                        <p className="text-2xl font-bold">{equipmentStats.total}</p>
                        <p className="text-xs text-muted-foreground">Total Items</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                      <CardContent className="p-4 text-center">
                        <CheckCircle className="h-6 w-6 mx-auto text-green-600 mb-1" />
                        <p className="text-2xl font-bold text-green-700">{equipmentStats.available}</p>
                        <p className="text-xs text-green-600">Available</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50">
                      <CardContent className="p-4 text-center">
                        <UserCheck className="h-6 w-6 mx-auto text-blue-600 mb-1" />
                        <p className="text-2xl font-bold text-blue-700">{equipmentStats.assigned}</p>
                        <p className="text-xs text-blue-600">Assigned</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-orange-50">
                      <CardContent className="p-4 text-center">
                        <Clock className="h-6 w-6 mx-auto text-orange-600 mb-1" />
                        <p className="text-2xl font-bold text-orange-700">{equipmentStats.maintenance || 0}</p>
                        <p className="text-xs text-orange-600">Maintenance</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Equipment Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Equipment</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Condition</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Assigned To</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {equipment.map(item => (
                        <tr key={item.equipment_id} className="hover:bg-muted/30">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium">{item.name}</p>
                              <p className="text-xs text-muted-foreground">{item.model}</p>
                              {item.serial_number && (
                                <p className="text-xs text-muted-foreground">S/N: {item.serial_number}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="capitalize">
                              {item.category === "laptop" && <Laptop className="h-3 w-3 mr-1" />}
                              {item.category === "monitor" && <Monitor className="h-3 w-3 mr-1" />}
                              {item.category}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={item.condition === "new" ? "default" : item.condition === "good" ? "secondary" : "outline"}>
                              {item.condition}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={item.status === "available" ? "success" : item.status === "assigned" ? "default" : "secondary"}>
                              {item.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {item.assigned_employee ? (
                              <div>
                                <p className="font-medium text-sm">{item.assigned_employee.full_name}</p>
                                <p className="text-xs text-muted-foreground">{item.assigned_employee.email}</p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {item.status === "available" && (
                                <Button size="sm" variant="outline" onClick={() => openAssignDialog(item)} data-testid={`assign-${item.equipment_id}`}>
                                  <UserPlus className="h-3 w-3 mr-1" />
                                  Assign
                                </Button>
                              )}
                              {item.status === "assigned" && (
                                <Button size="sm" variant="outline" onClick={() => handleUnassignEquipment(item.equipment_id)} data-testid={`unassign-${item.equipment_id}`}>
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Unassign
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteEquipment(item.equipment_id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {equipment.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                            <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>No equipment in inventory</p>
                            <p className="text-sm">Click "Seed Chromebooks" to add sample devices or "Add Equipment" to add manually</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Employee Dialog */}
      <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Employee</DialogTitle>
            <DialogDescription>Create a new employee record. A system account will be created automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Personal Information */}
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input value={employeeForm.full_name} onChange={(e) => setEmployeeForm(p => ({ ...p, full_name: e.target.value }))} placeholder="John Doe" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={employeeForm.email} onChange={(e) => setEmployeeForm(p => ({ ...p, email: e.target.value }))} placeholder="john@company.com" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={employeeForm.phone} onChange={(e) => setEmployeeForm(p => ({ ...p, phone: e.target.value }))} placeholder="(555) 123-4567" />
              </div>
            </div>
            
            {/* Employment Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select value={employeeForm.employment_type} onValueChange={(v) => setEmployeeForm(p => ({ ...p, employment_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full Time</SelectItem>
                    <SelectItem value="part_time">Part Time</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Hire Date *</Label>
                <Input type="date" value={employeeForm.hire_date} onChange={(e) => setEmployeeForm(p => ({ ...p, hire_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={employeeForm.department_id || "none"} onValueChange={(v) => setEmployeeForm(p => ({ ...p, department_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept.department_id} value={dept.department_id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input value={employeeForm.job_title} onChange={(e) => setEmployeeForm(p => ({ ...p, job_title: e.target.value }))} placeholder="e.g., Account Manager" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Manager</Label>
              <Select value={employeeForm.manager_id || "none"} onValueChange={(v) => setEmployeeForm(p => ({ ...p, manager_id: v === "none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Select manager (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {managers.map(mgr => (
                    <SelectItem key={mgr.hr_employee_id} value={mgr.hr_employee_id}>{mgr.full_name} {mgr.job_title ? `(${mgr.job_title})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Compensation */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Pay Type</Label>
                <Select value={employeeForm.salary_type} onValueChange={(v) => setEmployeeForm(p => ({ ...p, salary_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="salary">Salary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Rate/Salary ($)</Label>
                <Input type="number" value={employeeForm.salary_amount} onChange={(e) => setEmployeeForm(p => ({ ...p, salary_amount: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>Pay Frequency</Label>
                <Select value={employeeForm.pay_frequency} onValueChange={(v) => setEmployeeForm(p => ({ ...p, pay_frequency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Biweekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* System Account */}
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <input 
                type="checkbox" 
                id="create_system_account"
                checked={employeeForm.create_system_account} 
                onChange={(e) => setEmployeeForm(p => ({ ...p, create_system_account: e.target.checked }))}
                className="h-4 w-4"
              />
              <Label htmlFor="create_system_account" className="text-sm cursor-pointer">
                Create system account (allows employee to log in to portal)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmployeeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateEmployee} disabled={!employeeForm.full_name || !employeeForm.email || !employeeForm.hire_date}>Create Employee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Department Dialog */}
      <Dialog open={departmentDialogOpen} onOpenChange={setDepartmentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDepartment ? "Edit Department" : "Add Department"}</DialogTitle>
            <DialogDescription>Create departments to organize your employees</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Department Name *</Label>
              <Input value={departmentForm.name} onChange={(e) => setDepartmentForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Sales, Engineering, HR" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={departmentForm.description} onChange={(e) => setDepartmentForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description of this department" />
            </div>
            <div className="space-y-2">
              <Label>Department Manager</Label>
              <Select value={departmentForm.manager_id || "none"} onValueChange={(v) => setDepartmentForm(p => ({ ...p, manager_id: v === "none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Select manager (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {managers.map(mgr => (
                    <SelectItem key={mgr.hr_employee_id} value={mgr.hr_employee_id}>{mgr.full_name} {mgr.job_title ? `(${mgr.job_title})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDepartmentDialogOpen(false); setEditingDepartment(null); }}>Cancel</Button>
            <Button onClick={handleCreateDepartment} disabled={!departmentForm.name}>{editingDepartment ? "Update" : "Create"} Department</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Dialog */}
      <Dialog open={payrollDialogOpen} onOpenChange={setPayrollDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Payroll Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={payrollForm.employee_id} onValueChange={(v) => setPayrollForm(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.hr_employee_id} value={emp.hr_employee_id}>{emp.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Period Start *</Label>
                <Input type="date" value={payrollForm.pay_period_start} onChange={(e) => setPayrollForm(p => ({ ...p, pay_period_start: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Period End *</Label>
                <Input type="date" value={payrollForm.pay_period_end} onChange={(e) => setPayrollForm(p => ({ ...p, pay_period_end: e.target.value }))} />
              </div>
            </div>
            <Button variant="outline" onClick={handleCalculateFromClock} className="w-full">
              <Calculator className="h-4 w-4 mr-2" />
              Calculate from Clock Records
            </Button>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Regular Hours</Label>
                <Input type="number" value={payrollForm.regular_hours} onChange={(e) => setPayrollForm(p => ({ ...p, regular_hours: parseFloat(e.target.value) }))} />
              </div>
              <div className="space-y-2">
                <Label>Overtime Hours</Label>
                <Input type="number" value={payrollForm.overtime_hours} onChange={(e) => setPayrollForm(p => ({ ...p, overtime_hours: parseFloat(e.target.value) }))} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Gross Pay</Label>
                <Input type="number" value={payrollForm.gross_pay} onChange={(e) => setPayrollForm(p => ({ ...p, gross_pay: parseFloat(e.target.value) }))} />
              </div>
              <div className="space-y-2">
                <Label>Deductions</Label>
                <Input type="number" value={payrollForm.deductions} onChange={(e) => setPayrollForm(p => ({ ...p, deductions: parseFloat(e.target.value) }))} />
              </div>
              <div className="space-y-2">
                <Label>Net Pay</Label>
                <Input type="number" value={payrollForm.net_pay} onChange={(e) => setPayrollForm(p => ({ ...p, net_pay: parseFloat(e.target.value) }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayrollDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePayroll}>Create Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Onboarding Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={taskForm.title} onChange={(e) => setTaskForm(p => ({ ...p, title: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={taskForm.description} onChange={(e) => setTaskForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={taskForm.category} onValueChange={(v) => setTaskForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="documents">Documents</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="access">Access</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input type="number" value={taskForm.order} onChange={(e) => setTaskForm(p => ({ ...p, order: parseInt(e.target.value) }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTask}>Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Detail Dialog */}
      <Dialog open={employeeDetailOpen} onOpenChange={setEmployeeDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedEmployee && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedEmployee.full_name}</DialogTitle>
                <DialogDescription>{selectedEmployee.job_title} {selectedEmployee.department ? `- ${selectedEmployee.department}` : ''}</DialogDescription>
              </DialogHeader>
              <Tabs defaultValue="info">
                <TabsList>
                  <TabsTrigger value="info">Info</TabsTrigger>
                  <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
                </TabsList>
                <TabsContent value="info" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Email</Label>
                      <p>{selectedEmployee.email}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Phone</Label>
                      <p>{selectedEmployee.phone || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Employment Type</Label>
                      <p className="capitalize">{selectedEmployee.employment_type?.replace("_", " ")}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Manager</Label>
                      <p>{selectedEmployee.manager?.full_name || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Hire Date</Label>
                      <p>{selectedEmployee.hire_date}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Pay</Label>
                      <p>${selectedEmployee.salary_amount} / {selectedEmployee.salary_type === "hourly" ? "hr" : "yr"}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">PTO Balance</Label>
                      <p>{selectedEmployee.pto_balance || 0} hours</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Sick Balance</Label>
                      <p>{selectedEmployee.sick_balance || 0} hours</p>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="onboarding" className="space-y-2">
                  {selectedEmployee.onboarding_tasks?.sort((a, b) => (a.task?.order || 0) - (b.task?.order || 0)).map((task) => (
                    <div key={task.task_id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {task.completed ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <div className="w-5 h-5 border-2 rounded-full" />
                        )}
                        <div>
                          <p className={task.completed ? "line-through text-muted-foreground" : ""}>{task.task?.title}</p>
                          <p className="text-xs text-muted-foreground">{task.task?.category}</p>
                        </div>
                      </div>
                      {!task.completed && (
                        <Button size="sm" onClick={() => handleCompleteTask(selectedEmployee.hr_employee_id, task.task_id)}>
                          Complete
                        </Button>
                      )}
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Requirement Dialog */}
      <Dialog open={docReqDialogOpen} onOpenChange={setDocReqDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Document Requirement</DialogTitle>
            <DialogDescription>Define a document employees must provide during onboarding</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={docReqForm.name} onChange={(e) => setDocReqForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Government ID, Tax Form" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={docReqForm.description} onChange={(e) => setDocReqForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description of what's needed" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select value={docReqForm.document_type} onValueChange={(v) => setDocReqForm(p => ({ ...p, document_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="i9">I-9 Form</SelectItem>
                    <SelectItem value="w4">W-4 Form</SelectItem>
                    <SelectItem value="id">ID Document</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input type="number" value={docReqForm.order} onChange={(e) => setDocReqForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="doc_required" checked={docReqForm.required} onChange={(e) => setDocReqForm(p => ({ ...p, required: e.target.checked }))} className="h-4 w-4" />
              <Label htmlFor="doc_required">Required for onboarding</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocReqDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateDocReq} disabled={!docReqForm.name}>Create Requirement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Funding Dialog */}
      <Dialog open={fundingDialogOpen} onOpenChange={setFundingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Payroll Funds</DialogTitle>
            <DialogDescription>Deposit funds into the payroll account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount ($) *</Label>
              <Input type="number" value={fundingForm.amount} onChange={(e) => setFundingForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={fundingForm.description} onChange={(e) => setFundingForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g., Monthly payroll deposit" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFundingDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddFunding} disabled={fundingForm.amount <= 0} className="bg-green-600 hover:bg-green-700">Add Funds</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Commission Dialog */}
      <Dialog open={payCommissionDialogOpen} onOpenChange={setPayCommissionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pay Commission</DialogTitle>
            <DialogDescription>Pay commission to an agent from payroll account (Balance: ${payrollFunding.balance?.toFixed(2) || '0.00'})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Agent *</Label>
              <Select value={commissionForm.agent_id} onValueChange={(v) => setCommissionForm(p => ({ ...p, agent_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                <SelectContent>
                  {agents.map(a => (
                    <SelectItem key={a.agent_id} value={a.agent_id}>{a.full_name} ({a.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount ($) *</Label>
              <Input type="number" value={commissionForm.amount} onChange={(e) => setCommissionForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input value={commissionForm.description} onChange={(e) => setCommissionForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g., Q1 Sales Commission" />
            </div>
            <div className="space-y-2">
              <Label>Reference #</Label>
              <Input value={commissionForm.reference} onChange={(e) => setCommissionForm(p => ({ ...p, reference: e.target.value }))} placeholder="Optional reference number" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayCommissionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePayCommission} disabled={!commissionForm.agent_id || commissionForm.amount <= 0 || !commissionForm.description}>Pay Commission</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Employee Dialog */}
      <Dialog open={payEmployeeDialogOpen} onOpenChange={setPayEmployeeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pay Employee</DialogTitle>
            <DialogDescription>Pay an employee from payroll account (Balance: ${payrollFunding.balance?.toFixed(2) || '0.00'})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={payEmployeeForm.employee_id} onValueChange={(v) => setPayEmployeeForm(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.hr_employee_id} value={emp.hr_employee_id}>{emp.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount ($) *</Label>
                <Input type="number" value={payEmployeeForm.amount} onChange={(e) => setPayEmployeeForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Payment Type</Label>
                <Select value={payEmployeeForm.payment_type} onValueChange={(v) => setPayEmployeeForm(p => ({ ...p, payment_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular Pay</SelectItem>
                    <SelectItem value="bonus">Bonus</SelectItem>
                    <SelectItem value="commission">Commission</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={payEmployeeForm.description} onChange={(e) => setPayEmployeeForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g., Biweekly payroll" />
            </div>
            <div className="space-y-2">
              <Label>Reference #</Label>
              <Input value={payEmployeeForm.reference} onChange={(e) => setPayEmployeeForm(p => ({ ...p, reference: e.target.value }))} placeholder="Optional reference number" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayEmployeeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePayEmployee} disabled={!payEmployeeForm.employee_id || payEmployeeForm.amount <= 0}>Process Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Employee Documents Dialog */}
      <Dialog open={viewDocsDialogOpen} onOpenChange={setViewDocsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedEmployeeDocs && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedEmployeeDocs.full_name} - Documents</DialogTitle>
                <DialogDescription>
                  {selectedEmployeeDocs.completion?.completed || 0} of {selectedEmployeeDocs.completion?.total || 0} required documents uploaded
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {selectedEmployeeDocs.requirements?.sort((a, b) => a.order - b.order).map((req) => (
                  <div key={req.requirement_id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="capitalize">{req.document_type}</Badge>
                        <div>
                          <p className="font-medium">{req.name}</p>
                          <p className="text-sm text-muted-foreground">{req.description}</p>
                        </div>
                      </div>
                      {req.required && <Badge variant="secondary">Required</Badge>}
                    </div>
                    {req.uploaded ? (
                      <div className="bg-muted p-3 rounded-md mt-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">{req.filename}</p>
                            <p className="text-xs text-muted-foreground">Uploaded: {new Date(req.uploaded_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={req.status === "approved" ? "bg-green-100 text-green-800" : req.status === "rejected" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}>
                              {req.status}
                            </Badge>
                            <Button size="sm" variant="outline" onClick={() => downloadDocument(selectedEmployeeDocs.hr_employee_id, req.document_id, req.filename)}>
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {req.status === "pending_review" && (
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleUpdateDocStatus(selectedEmployeeDocs.hr_employee_id, req.document_id, "approved")}>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleUpdateDocStatus(selectedEmployeeDocs.hr_employee_id, req.document_id, "rejected")}>
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-muted/50 p-3 rounded-md mt-2 text-center text-muted-foreground">
                        <Upload className="h-5 w-5 mx-auto mb-1" />
                        <p className="text-sm">Not uploaded yet</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={editEmployeeDialogOpen} onOpenChange={setEditEmployeeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogDescription>Update employee information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={editEmployeeForm.full_name} onChange={(e) => setEditEmployeeForm(p => ({ ...p, full_name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={editEmployeeForm.email} onChange={(e) => setEditEmployeeForm(p => ({ ...p, email: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editEmployeeForm.phone} onChange={(e) => setEditEmployeeForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input value={editEmployeeForm.job_title} onChange={(e) => setEditEmployeeForm(p => ({ ...p, job_title: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={editEmployeeForm.department_id || "none"} onValueChange={(v) => setEditEmployeeForm(p => ({ ...p, department_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d.department_id} value={d.department_id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Manager</Label>
                <Select value={editEmployeeForm.manager_id || "none"} onValueChange={(v) => setEditEmployeeForm(p => ({ ...p, manager_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {managers.map(m => (
                      <SelectItem key={m.hr_employee_id} value={m.hr_employee_id}>{m.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select value={editEmployeeForm.employment_type} onValueChange={(v) => setEditEmployeeForm(p => ({ ...p, employment_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full Time</SelectItem>
                    <SelectItem value="part_time">Part Time</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editEmployeeForm.status} onValueChange={(v) => setEditEmployeeForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Salary Type</Label>
                <Select value={editEmployeeForm.salary_type} onValueChange={(v) => setEditEmployeeForm(p => ({ ...p, salary_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="salary">Salary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Salary Amount ($)</Label>
                <Input type="number" value={editEmployeeForm.salary_amount} onChange={(e) => setEditEmployeeForm(p => ({ ...p, salary_amount: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>PTO Balance (hours)</Label>
                <Input type="number" value={editEmployeeForm.pto_balance} onChange={(e) => setEditEmployeeForm(p => ({ ...p, pto_balance: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>Sick Balance (hours)</Label>
                <Input type="number" value={editEmployeeForm.sick_balance} onChange={(e) => setEditEmployeeForm(p => ({ ...p, sick_balance: parseFloat(e.target.value) || 0 }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmployeeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateEmployee} disabled={!editEmployeeForm.full_name || !editEmployeeForm.email}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stripe Funding Dialog */}
      <Dialog open={stripeFundingDialogOpen} onOpenChange={setStripeFundingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Payroll Funds via Card</DialogTitle>
            <DialogDescription>Securely add funds using Stripe payment processing</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount ($) *</Label>
              <Input 
                type="number" 
                value={stripeFundingAmount} 
                onChange={(e) => setStripeFundingAmount(parseFloat(e.target.value) || 0)} 
                placeholder="1000.00"
                min="1"
              />
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <span className="font-medium">Secure Payment</span>
              </div>
              <p className="text-sm text-muted-foreground">
                You will be redirected to Stripe's secure checkout page to complete the payment.
                All major credit and debit cards accepted.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStripeFundingDialogOpen(false)} disabled={processingStripe}>Cancel</Button>
            <Button onClick={handleStripeFunding} disabled={stripeFundingAmount <= 0 || processingStripe} className="bg-green-600 hover:bg-green-700">
              {processingStripe ? "Redirecting..." : `Pay $${stripeFundingAmount.toFixed(2)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Equipment Dialog */}
      <Dialog open={equipmentDialogOpen} onOpenChange={setEquipmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Equipment</DialogTitle>
            <DialogDescription>Add new equipment to the inventory</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input 
                  value={equipmentForm.name} 
                  onChange={(e) => setEquipmentForm(p => ({ ...p, name: e.target.value }))} 
                  placeholder="e.g., Dell Laptop"
                  data-testid="equipment-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Model *</Label>
                <Input 
                  value={equipmentForm.model} 
                  onChange={(e) => setEquipmentForm(p => ({ ...p, model: e.target.value }))} 
                  placeholder="e.g., Latitude 5520"
                  data-testid="equipment-model"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input 
                  value={equipmentForm.serial_number} 
                  onChange={(e) => setEquipmentForm(p => ({ ...p, serial_number: e.target.value }))} 
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={equipmentForm.category} onValueChange={(v) => setEquipmentForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="laptop">Laptop</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="monitor">Monitor</SelectItem>
                    <SelectItem value="peripheral">Peripheral</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Condition</Label>
                <Select value={equipmentForm.condition} onValueChange={(v) => setEquipmentForm(p => ({ ...p, condition: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Purchase Date</Label>
                <Input 
                  type="date"
                  value={equipmentForm.purchase_date} 
                  onChange={(e) => setEquipmentForm(p => ({ ...p, purchase_date: e.target.value }))} 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Purchase Price ($)</Label>
                <Input 
                  type="number"
                  value={equipmentForm.purchase_price} 
                  onChange={(e) => setEquipmentForm(p => ({ ...p, purchase_price: parseFloat(e.target.value) || 0 }))} 
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={equipmentForm.notes} 
                onChange={(e) => setEquipmentForm(p => ({ ...p, notes: e.target.value }))} 
                placeholder="Specs, warranty info, etc."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEquipmentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddEquipment} disabled={!equipmentForm.name || !equipmentForm.model} data-testid="save-equipment-btn">
              Add Equipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Equipment Dialog */}
      <Dialog open={assignEquipmentOpen} onOpenChange={setAssignEquipmentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Equipment</DialogTitle>
            <DialogDescription>
              Assign {selectedEquipment?.name} ({selectedEquipment?.model}) to an employee
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Employee *</Label>
              <Select value={assignForm.employee_id || "none"} onValueChange={(v) => setAssignForm(p => ({ ...p, employee_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="assign-employee-select">
                  <SelectValue placeholder="Choose an employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select employee</SelectItem>
                  {employees.filter(e => e.status !== "terminated").map(emp => (
                    <SelectItem key={emp.hr_employee_id} value={emp.hr_employee_id}>
                      {emp.full_name} ({emp.job_title || "No title"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea 
                value={assignForm.notes} 
                onChange={(e) => setAssignForm(p => ({ ...p, notes: e.target.value }))} 
                placeholder="Any notes about this assignment"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignEquipmentOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignEquipment} disabled={!assignForm.employee_id} data-testid="confirm-assign-btn">
              Assign Equipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
